import React from 'react';
import { useHistory, Redirect, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  Button,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  InputAdornment,
} from '@material-ui/core';
import { Menu as MenuIcon, AccountCircle, ArrowBack, Language as LanguageIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import AppContext from 'contexts/AppContext';
import { getLanguages } from 'services/languageServices';
import Language from 'types/models/Language';
import UserRoleType from 'types/enums/UserRoleType';
import AuthData from 'types/models/AuthData';
import StorageType from 'types/enums/StorageType';
import useSetupInitialState from 'hooks/useSetupInitialState';
import 'styles/components/AppHeader.scss';
import '../translation/config';

const getRoleName = (authData: AuthData | null): string => {
  if (!authData?.user.role || !authData) return '';
  if (authData.user.role === UserRoleType.VenueEditor) {
    return 'roles.venue-editor';
  } else if (authData.user.role === UserRoleType.CommerceEditor) {
    return 'roles.commerce-editor';
  } else if (authData.user.role === UserRoleType.FranchiseEditor) {
    return 'roles.franchise-editor';
  }

  return 'roles.admin';
};

export default (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { authData, setAuthData, setCurrentPageListNumber } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const [toggleDrawer, setToggleDrawer] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [languages, setLanguages] = React.useState<Language[]>();

  const open = Boolean(anchorEl);
  const role = authData?.user?.role;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = () => {
    setAuthData(null);
    const ls = [StorageType.AuthData, StorageType.CurrentCommerce, StorageType.CurrentVenue];
    ls.forEach((item) => localStorage.removeItem(item));
    handleClose();
    return <Redirect to="/public/login" />;
  };

  const goToPage = (route: string) => {
    history.push(route);
    setToggleDrawer(false);
  };

  const loadLanguages = React.useCallback(async () => {
    const languages = await getLanguages();
    if (languages) setLanguages(languages);
  }, []);

  const handleChangeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const showBackButton = !(
    history.location.pathname.includes('private/welcome') ||
    location.pathname.includes('private/welcome') ||
    window.location.pathname.includes('private/welcome') ||
    window.history.length === 1
  );

  React.useEffect(() => {
    loadLanguages();
  }, []);

  const LanguageSelector = (): JSX.Element => {
    return (
      <Select
        className="language-selector"
        displayEmpty
        startAdornment={
          <InputAdornment position="start">
            <LanguageIcon />
          </InputAdornment>
        }
        style={{ color: '#FFF' }}
      >
        <MenuItem>{t('app-header-language')}</MenuItem>
        {languages?.map((language) => (
          <MenuItem
            onClick={() => {
              handleChangeLanguage(language.code);
            }}
            value={language.id}
            key={language.id}
          >
            {language.description}
          </MenuItem>
        ))}
      </Select>
    );
  };

  return (
    <AppBar position="static">
      <Toolbar style={{ maxHeight: 56 }}>
        <>
          {authData && (
            <>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => {
                  setToggleDrawer(true);
                }}
              >
                <MenuIcon />
              </IconButton>
              {showBackButton && (
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => {
                    history.go(-1);
                    setCurrentPageListNumber(1);
                  }}
                >
                  <ArrowBack />
                </IconButton>
              )}
              {entity && <p className="entity-name">{entity.name}</p>}
              <header className="app-header">
                <div className="app-header-content">
                  <LanguageSelector />

                  <span className="separator" />

                  <div className="user-info">
                    <div className="user-text">
                      <Tooltip title={<span style={{ fontSize: 14 }}>{authData.user.name}</span>}>
                        <p className="user-name">{authData.user.name || '-'}</p>
                      </Tooltip>
                      <p className="user-role">{t(getRoleName(authData))}</p>
                    </div>

                    <IconButton onClick={handleMenu} color="inherit" className="p-0">
                      <AccountCircle style={{ fontSize: 32 }} />
                    </IconButton>
                  </div>
                </div>

                <Menu id="menu-appbar" anchorEl={anchorEl} open={open} onClose={handleClose}>
                  <MenuItem onClick={handleLogOut}>{t('app-header-logout')}</MenuItem>
                </Menu>
              </header>
            </>
          )}
        </>
      </Toolbar>
      <Drawer
        anchor="left"
        open={toggleDrawer}
        onClose={() => {
          setToggleDrawer(false);
        }}
      >
        {
          //  <Button
          //    onClick={() => {
          //      goToPage('/');
          //    }}
          //  >
          //    {t('app-header-home')}
          //  </Button>
        }
        {(authData?.user?.role === UserRoleType.Admin || authData?.user.role === UserRoleType.VenueEditor) && (
          <>
            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/venues');
              }}
            >
              {t('app-header-venues')}
            </Button>
            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/benefits');
              }}
            >
              {t('app-header-benefits')}
            </Button>
          </>
        )}
        {(authData?.user?.role === UserRoleType.Admin || authData?.user?.role === UserRoleType.FranchiseEditor) && (
          <Button
            onClick={() => {
              setCurrentPageListNumber(1);
              goToPage('/private/franchises');
            }}
          >
            {t('app-header-franchise')}
          </Button>
        )}
        <Button
          onClick={() => {
            setCurrentPageListNumber(1);
            goToPage('/private/commerces');
          }}
        >
          {t('app-header-commerces')}
        </Button>
        {authData?.user?.role === UserRoleType.Admin && (
          <>
            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/users');
              }}
            >
              {t('app-header-users')}
            </Button>
            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/admin/commerce-categories');
              }}
            >
              {t('app-header-commerces-categories')}
            </Button>

            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/admin/product-categories');
              }}
            >
              {t('app-header-products')}
            </Button>

            <Button
              onClick={() => {
                goToPage('/private/admin/payments');
              }}
            >
              {t('app-payment-methods')}
            </Button>
            <Button
              onClick={() => {
                setCurrentPageListNumber(1);
                goToPage('/private/admin/twitter-configs');
              }}
            >
              {t('app-header-twitter')}
            </Button>
          </>
        )}
        {(role === UserRoleType.Admin || role === UserRoleType.VenueEditor) && (
          <Button
            onClick={() => {
              goToPage('/private/protocol-orders');
            }}
          >
            {t('app-header-orders')}
          </Button>
        )}
        <Button
          onClick={() => {
            goToPage('/private/reporting');
          }}
        >
          {t('app-header-reports')}
        </Button>
        {[UserRoleType.VenueEditor, UserRoleType.CommerceEditor].includes(role as UserRoleType) && (
          <Button
            onClick={() => {
              goToPage('/private/billing-orders');
            }}
          >
            {t('app-header-billing')}
          </Button>
        )}
        {role === UserRoleType.Admin && (
          <Button
            onClick={() => {
              goToPage('/private/admin/system-config');
            }}
          >
            {t('app-header-system-config')}
          </Button>
        )}
      </Drawer>
    </AppBar>
  );
};
